import type { Messages } from "./es";

const en = {
  // ── Common ─────────────────────────────────────────────────────────
  commonPoweredBy: "Powered by",
  commonManageReservationLink: "Manage my reservation",
  commonRestaurantConfirmAvailability:
    "The restaurant will confirm availability.",
  commonConnectionErrorShort: "Connection error",
  commonConnectionErrorRetry: "Connection error. Please try again.",
  commonConnectionErrorLater: "Connection error. Please try again later.",
  commonPeopleOne: "{n} person",
  commonPeopleOther: "{n} people",
  commonPeopleShortOne: "{n} guest",
  commonPeopleShortOther: "{n} guests",
  commonCalendarWeekdays: ["M", "T", "W", "T", "F", "S", "S"],
  commonTimeSuffix: "",

  commonMenuShowDescription: "Show description",
  commonMenuHideDescription: "Hide description",
  commonMenuAllGuestsLabel: "For the whole table ({count} guests)",
  commonMenuTotalLabel: "Selected: {total} of {count} guests",
  commonMenuExceedsPartySize: "The selected menu exceeds the number of guests.",
  commonMenuSummaryItem: "{qty}× {name}",

  // ── App.tsx (table booking flow) ─────────────────────────────────────
  appOrgNotFoundHeading: "Restaurant not found",
  appOrgNotFoundFallback: "Restaurant not found",
  appOrgLoadNetworkError: "Error loading the restaurant's details",
  appOrgLoadGenericError: "The restaurant's details could not be loaded",

  appSuccessHeadingLargeGroup: "Request received!",
  appSuccessHeadingPending: "Request sent!",
  appSuccessHeadingConfirmed: "Reservation confirmed!",
  appSuccessBodyLargeGroup:
    "Your group request at {name} has been registered. The restaurant will get in touch with you.",
  appSuccessBodyPending:
    "Your reservation at {name} is awaiting approval. We'll let you know as soon as the restaurant confirms it.",
  appSuccessBodyConfirmed: "Your reservation at {name} has been registered.",
  appSuccessEmailNotePending: "You'll receive the confirmation notice at {email}",
  appSuccessEmailNoteConfirmed:
    "You'll receive a confirmation email at {email}",
  appNewReservationButton: "Make another reservation",

  appPartySizeLabel: "Number of guests",
  appLargeGroupButton: "Large group (+{threshold} people)",
  appLargeGroupQuestion: "How many of you are there?",
  appLargeGroupPlaceholder: "e.g. 25",

  appSelectDayLabel: "Select a day",
  appShowMoreDates: "See more dates",

  appSelectRoomLabel: "Select a room",
  appNoRoomsAvailable: "No rooms available for this day.",

  appSelectTimeLabel: "Select a time",
  appDefaultClosedReason: "Closed",
  appNoSlotsAvailable: "No time slots available",
  appDefaultShiftLunch: "LUNCH",
  appDefaultShiftDinner: "DINNER",
  appNoTablesAvailable: "No tables available",
  appContinueButton: "Continue",

  appMenuQuestionHeading: "Would you like to order a menu?",
  appMenuNoThanksButton: "No, thanks",
  appMenuYesButton: "Yes",
  appMenuListHeading: "Choose your menu",
  appMenuSummaryHeading: "Your menu:",

  appSummaryModify: "Edit",
  appPhoneLabel: "Phone *",
  appPhonePlaceholder: "202 555 0143",
  appNameLabel: "First name *",
  appNamePlaceholder: "Your first name",
  appLastNameLabel: "Last name *",
  appLastNamePlaceholder: "Your last name",
  appEmailLabel: "Email *",
  appEmailPlaceholder: "you@email.com",
  appEmailInvalid: "Please enter a valid email address",
  appNotesLabel: "Comments (optional)",
  appNotesPlaceholderDefault:
    "Table preferences, a birthday, a high chair, reduced mobility, etc.",

  appAcceptTermsPrefix: "I accept the",
  appTermsLinkLabel: "Terms and Conditions",
  appAcceptPrivacyPrefix: "I accept the",
  appPrivacyLinkLabel: "Privacy Policy",
  appSpendNotice:
    "{restaurant} keeps the amount of your reservation's bill on your customer record. You can object at any time. More information:",
  appMarketingCheckbox: "Receive offers and news from the restaurant",

  appLargeGroupSummary: "Large group ({people})",

  appBackButton: "Back",
  appSubmitButton: "Book",
  appSubmitting: "Sending...",
  appSubmitErrorFallback: "Error creating the reservation",

  appFullCalendarTitle: "Select a date",

  // ── ReservationManage.tsx (manage a reservation from the link) ───────
  manageLoading: "Loading your reservation...",
  manageNotFoundHeading: "Reservation not found",
  manageFetchErrorFallback: "Reservation not found",
  manageConfirmErrorFallback: "Error confirming",
  manageCancelErrorFallback: "Error cancelling",
  manageModifyErrorFallback: "Error changing the reservation",

  statusPending: "Awaiting confirmation",
  statusConfirmed: "Confirmed",
  statusCancelled: "Cancelled",
  statusCompleted: "Completed",
  statusNoShow: "No Show",
  statusCancelledByEstablishment: "Cancelled by the restaurant",

  manageGoogleEventTitle: "Reservation at {name}",
  manageGoogleDetailsIntro: "Reservation for {people}",
  manageGoogleDetailsCode: "Code: {code}",
  manageGoogleDetailsNotes: "Notes: {notes}",

  manageConfirmSuccessHeading: "Reservation confirmed!",
  manageConfirmSuccessBody: "See you on {date} at {time}",
  manageAddToGoogleCalendar: "Add to Google Calendar",

  manageCancelSuccessHeading: "Reservation cancelled",
  manageCancelSuccessBodyLine1: "Your reservation has been cancelled.",
  manageCancelSuccessBodyLine2: "We hope to see you again soon.",

  manageModifySuccessHeading: "Reservation changed",
  manageModifySuccessBodyLine1: "Your change request has been sent.",
  manageModifySuccessBodyLine2: "You'll receive confirmation soon.",

  manageCancelledByRestaurantTitle:
    "The restaurant has cancelled this reservation",
  manageCancelledByRestaurantBody:
    "We're sorry for the inconvenience. Feel free to book another day or contact the restaurant directly.",
  manageCancellationReasonLabel: "Reason given by the restaurant",

  manageYourReservationHeading: "Your reservation",
  manageRoomLabel: "Room: {roomName}",
  manageCodeLabelBlock: "Reservation code",
  manageNotesLabel: "Notes",

  manageMenuHeading: "Your menu",
  manageMenuChangeButton: "Change",
  manageMenuNoneSelected: "You haven't added a menu to your reservation.",
  manageMenuNotEditableNotice:
    "The menu choice can be changed until the day before the reservation.",
  manageMenuModalTitle: "Change menu",
  manageMenuSaveErrorFallback: "Error saving the menu",

  managePendingApprovalBannerTitle: "Awaiting approval from the restaurant.",
  managePendingApprovalBannerBody:
    "Once they confirm it you'll get a notice and be able to confirm your attendance here.",
  manageConfirmDisabledTitle:
    "Available once the restaurant approves your request",
  manageConfirmDisabledLabel: "Confirm attendance (awaiting approval)",
  manageConfirmButton: "Confirm attendance",

  managePendingChangeBannerTitle: "You already have a change request pending.",
  managePendingChangeBannerFooter:
    "The restaurant will review it and confirm shortly.",
  managePendingChangeDisabledTitle:
    "You already have a change request awaiting review",
  managePendingChangeDisabledLabel: "Change request pending",
  manageModifyButton: "Change reservation",
  manageCancelButton: "Cancel reservation",

  managePendingSummaryDate: "date: {date}",
  managePendingSummaryTime: "time: {time}",
  managePendingSummaryPeople: "people: {n}",

  manageWhatsAppButton: "Contact via WhatsApp",

  manageModalConfirmTitle: "Confirm attendance",
  manageModalConfirmQuestion:
    "Do you confirm you'll attend your reservation at {name}?",
  manageModalConfirmBack: "Back",
  manageModalConfirmSubmit: "Confirm",

  manageModalCancelTitle: "Cancel reservation",
  manageModalCancelWarningTitle: "Are you sure?",
  manageModalCancelWarningBody:
    "This can't be undone. If you cancel, you'll lose your reservation at {name}.",
  manageModalCancelKeep: "Keep it",
  manageModalCancelSubmit: "Cancel",

  manageModalEditTitle: "Change reservation",
  manageModalEditDateLabel: "Date",
  manageModalEditTimeLabel: "Time",
  manageModalEditPartyLabel: "Number of guests",
  manageModalEditSummaryPrefix: "New reservation:",
  manageModalEditSummaryAt: "at",
  manageModalEditSummaryFor: "for",
  manageModalEditCancel: "Cancel",
  manageModalEditSubmit: "Save changes",

  // ── ConfirmReservation.tsx (confirm from email/WhatsApp) ─────────────
  confirmLoading: "Confirming your reservation...",
  confirmErrorHeading: "Couldn't confirm",
  confirmAlreadyCancelled: "This reservation has already been cancelled.",
  confirmGenericErrorFallback: "Error confirming the reservation",

  confirmAwaitingHeading: "Awaiting approval",
  confirmAwaitingBody:
    "Your reservation is awaiting approval from the restaurant. Once they confirm it you'll get a notice by email or WhatsApp and will be able to reconfirm it here.",
  confirmCodeLabelInline: "Reservation code:",

  confirmAlreadyHeading: "Reservation already confirmed",
  confirmSuccessHeading: "Reservation confirmed!",
  confirmAlreadyBody: "Your reservation was already confirmed earlier.",
  confirmSuccessBody: "Thanks for confirming your attendance.",
  confirmFooterText: "See you soon. Enjoy your meal!",

  classesPickDay: "Pick a day",
  classesWeekPrev: "Previous week",
  classesWeekNext: "Next week",
  classesCountOne: "{n} class",
  classesCountOther: "{n} classes",
  classesNoClassesThisDay: "No classes on this day",
  classesSeatsLeft: "{n} of {total} spots left",
  classesFull: "Full",
  classesMinutes: "{n} min",
  classesTeacher: "Teacher: {name}",
  classesPeople: "People",
  classesName: "Full name",
  classesEmail: "Email",
  classesPhone: "Phone (optional)",
  classesMarketing: "I want to receive news from this business",
  classesSubmit: "Confirm booking",
  classesSubmitting: "Booking…",
  classesFieldsError: "Check your name and email",
  classesOnlyLeft: "Only {n} spots left in this class",
  classesFullNow: "This class just filled up. Pick another time or day.",
  classesSuccessTitle: "Booking confirmed!",
  classesSuccessCode: "Booking code",
  classesSuccessEmailSent:
    "We've sent you an email with the details and a link to manage your booking.",
  classesNewBooking: "Make another booking",
  classesLoadError: "Couldn't load the information. Please try again.",
  classesCompanyToggle: "I'm booking on behalf of a company (hotel, agency…)",
  classesCompanyName: "Company or hotel name",
} satisfies Messages;

export default en;
